import React from 'react'
import { useStyles } from '../../style/views'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import Fade from '@mui/material/Fade'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import { useHistory } from 'react-router-dom'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import { useAuth0 } from '../../contexts/auth0-context'
import { setStrategy } from '../../redux/actions'
import { connect } from 'react-redux'
import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder'
import { useGetSnapshotsQuery } from '../../shared/queries/useGetSnapshotsQuery'
import { useQueryClient } from 'react-query'
import { SNAPSHOTS_QUERY_KEY } from '../../shared/queries/useGetSnapshotsQuery'
import { useStrategy } from '../../shared/hooks/useStrategy'
import { NO_VALUE_LABEL } from '../../shared/utils/displayValue'

function mapDispatchToProps(dispatch) {
    return {
        setStrategy: (strat) => dispatch(setStrategy(strat)),
    }
}

const columns = [
    { id: 'status', label: 'Status' },
    { id: 'createdAt', label: 'Snapshot Date' },
    { id: 'note', label: 'Note' },
    { id: 'creatorName', label: 'Created By' },
    { id: 'action', label: '', align: 'center' },
]

function ConnectedSnapshotTable(props) {
    const classes = useStyles()
    const history = useHistory()
    const { getTokenSilently } = useAuth0()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const queryClient = useQueryClient()
    const strategy = useStrategy()
    const sourceId = strategy?.sourceId ?? strategy?._id

    const { data: items = [], isLoading } = useGetSnapshotsQuery()

    const completedSnapshots = items
        .filter((item) => !item.isSnapshotJob)
        .sort((a, b) => (a.createdAt < b.createdAt) - (a.createdAt > b.createdAt))

    const jobItems = items.filter((item) => item.isSnapshotJob)

    const allRows = [...jobItems, ...completedSnapshots]

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }

    const handleDismissJob = async (jobId) => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        await callAPI(endpoints.dismissSnapshotJob.url + jobId, endpoints.dismissSnapshotJob.method, token)
        queryClient.invalidateQueries([SNAPSHOTS_QUERY_KEY, sourceId])
    }

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        })

    const isSnapshotDataEmpty = !isLoading && allRows.length === 0

    return (
        <Fade in={!isLoading} timeout={800}>
            <div className={classes.root}>
                {isSnapshotDataEmpty && (
                    <EmptyPlaceholder
                        title="No Snapshots to display"
                        subtitle="Select 'Save Snapshot' under 'Actions' menu to capture a new Snapshot"
                        sx={{ mt: 6 }}
                    />
                )}
                {!isSnapshotDataEmpty && (
                    <>
                        <TableContainer className={classes.container}>
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.align}
                                                style={{ fontSize: '1vw', minWidth: column.minWidth }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {allRows
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => {
                                            if (row.isSnapshotJob) {
                                                return (
                                                    <TableRow key={row._id} hover tabIndex={-1}>
                                                        <TableCell style={{ fontSize: '.9vw' }}>
                                                            {row.status === 'in_progress' ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <Chip label="Failed" variant="outlined" size="small" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell style={{ fontSize: '.9vw' }}>
                                                            {row.status === 'failed'
                                                                ? formatDate(row.createdAt)
                                                                : NO_VALUE_LABEL}
                                                        </TableCell>
                                                        <TableCell style={{ fontSize: '.9vw' }}>
                                                            {row.status === 'failed' ? row.error : NO_VALUE_LABEL}
                                                        </TableCell>
                                                        <TableCell style={{ fontSize: '.9vw' }}>
                                                            {row.status === 'failed'
                                                                ? row.creatorName || NO_VALUE_LABEL
                                                                : NO_VALUE_LABEL}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {row.status === 'failed' && (
                                                                <Tooltip title="Dismiss">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDismissJob(row._id)}
                                                                    >
                                                                        <CloseIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            }

                                            return (
                                                <TableRow hover role="checkbox" tabIndex={-1} key={row._id}>
                                                    <TableCell style={{ fontSize: '.9vw' }}>
                                                        <Chip label="Success" color="primary" size="small" />
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '.9vw' }}>
                                                        {formatDate(row.createdAt)}
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '.9vw' }}>
                                                        {row.snapshotNote || NO_VALUE_LABEL}
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '.9vw' }}>
                                                        {row.creatorName || NO_VALUE_LABEL}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <ArrowForwardIcon
                                                            color="primary"
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                props.setStrategy(row)
                                                                history.push('/')
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 100]}
                            component="div"
                            count={allRows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            style={{ color: 'white' }}
                        />
                    </>
                )}
            </div>
        </Fade>
    )
}

const SnapshotTable = connect(null, mapDispatchToProps)(ConnectedSnapshotTable)

export default SnapshotTable
